import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Message } from "@/types/chat";
import { User } from "@supabase/supabase-js";

export function useSendMessage(user: User | null) {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const sendMessage = async (
    message: string,
    conversationId: string | null,
    messages: Message[]
  ) => {
    console.log('Starting sendMessage with:', {
      messageLength: message.length,
      conversationId,
      userId: user?.id,
      messagesCount: messages.length
    });

    if (!message.trim() || !conversationId || !user || isLoading) {
      console.log('Message validation failed:', {
        messageEmpty: !message.trim(),
        noConversation: !conversationId,
        noUser: !user,
        isLoading
      });
      return;
    }

    setIsLoading(true);
    console.log('Sending message to conversation:', conversationId);

    try {
      // First store the user's message
      const { data: messageData, error: messageError } = await supabase
        .from('chat_messages')
        .insert({
          conversation_id: conversationId,
          content: message,
          role: 'user',
          user_id: user.id,
        })
        .select()
        .single();

      if (messageError) {
        console.error('Error storing user message:', messageError);
        throw messageError;
      }

      console.log('User message stored successfully:', messageData);

      // Then get AI response
      const { data: response, error: functionError } = await supabase.functions.invoke(
        'chat-completion',
        {
          body: {
            messages: [...messages, { role: 'user', content: message }].map(({ role, content }) => ({
              role,
              content,
            })),
            conversationId,
            user_id: user.id,
          },
        }
      );

      console.log('Edge function response:', response);

      if (functionError) {
        console.error('Edge function error:', functionError);
        throw functionError;
      }

    } catch (error: any) {
      console.error('Error in sendMessage:', error);
      toast({
        variant: "destructive",
        title: "Error sending message",
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoading,
    sendMessage,
  };
}